'use client';

import { twMerge } from '@shared/themes';
import { type Currency, ROUTES } from '@shared/utils';
import { useTranslations } from 'next-intl';
import { type ComponentProps, useState } from 'react';
import { DyjDisabledMessage, getLinesWinnings } from '..';
import { Link } from '../../Link/Link.tsx';
import { DrawSelector } from '../../lotteries/DrawSelector/DrawSelector';
import { TotalWinnings } from '../../lotteries/TotalWinnings/TotalWinnings';
import { getLotteryDrawFrequency, shouldShowDrawingIdInDrawSelector } from '../../lotteries/utils';
import { PurchaseResultsSection } from '../PurchaseResultsSection/PurchaseResultsSection';
import type { LotteryName, Participation } from '../types';
import { useParticipationDate } from './hooks';

type ParticipationInfoProps = ComponentProps<'div'> & {
  participations: Participation[];
  selectedDate: string;
  dyjPlayed: boolean;
  onDateSelect: (date: Date) => void;
  currency: Currency;
  lotteryName: LotteryName;
  options: string[];
  hasMoreHistoricalParticipations?: boolean;
  showOlderParticipationsInfo?: boolean;
  setShowOlderParticipationsInfo?: (showOlderParticipationsInfo: boolean) => void;
};

/**
 * ParticipationInfo is the component, that is responsible to render information about participation from the API.
 * Key features of the component:
 *  - DrawSelector - will switch between participations, in case of multiple participations available for the ticket;
 *  - DyjDisabledMessage - will be rendered, when user played DYJ and it was disabled for selected participation;
 *  - PurchaseResultsSection - will render result of the current draw, when available;
 *
 * Extends HTMLDivElement props
 *
 * @param participations - Array of participations for ticket
 * @param selectedDate - Currently selected draw date from available participations
 * @param onDateSelect - Handler for draw date change
 * @param dyjPlayed - Indicates whether DYJ was played in current ticket
 * @param currency - Currency to be used
 * @param lotteryName - Selected lottery name
 * @param options - DetailedTicket options
 * @param hasMoreHistoricalParticipations - Tells if ticket has participations older than 13 months (not included in DetailedTicket)
 * @param showOlderParticipationsInfo - Controlled state for the older participations help panel
 * @param setShowOlderParticipationsInfo - Controlled setter for the older participations help panel
 * @returns HTMLDivElement with rendered features
 */
function ParticipationInfo({
  className,
  participations,
  selectedDate,
  onDateSelect,
  dyjPlayed,
  currency,
  lotteryName,
  options,
  hasMoreHistoricalParticipations,
  showOlderParticipationsInfo,
  setShowOlderParticipationsInfo,
  ...props
}: ParticipationInfoProps) {
  const t = useTranslations('lotteries');
  const [internalShowOlderParticipationsInfo, setInternalShowOlderParticipationsInfo] = useState(false);
  const isShowingOlderParticipationsInfo = showOlderParticipationsInfo ?? internalShowOlderParticipationsInfo;
  const setIsShowingOlderParticipationsInfo = setShowOlderParticipationsInfo ?? setInternalShowOlderParticipationsInfo;
  const shouldShowDrawingId = shouldShowDrawingIdInDrawSelector(lotteryName);

  const participationDates = useParticipationDate(
    participations.map(({ drawingId, drawingDate, participationState }) => ({
      drawingId: shouldShowDrawingId ? drawingId.replace(`${lotteryName}_`, '') : '',
      drawingDate,
      participationState,
    }))
  );

  const draws = participationDates.map(draw => ({
    ...draw,
    isSelected: isShowingOlderParticipationsInfo ? false : draw.drawingDate === selectedDate,
  }));

  const participation = participations.find(({ drawingDate }: Participation) => drawingDate === selectedDate)!;

  const { participationState, drawingDate, dyjCancelled, drawingId, drawingState } = participation;

  const totalWinnings = getLinesWinnings(lotteryName, participation, options, currency);
  const isCalculating = participationState !== 'FINISHED' && ['RESULTS_PRIZES', 'RESULTS_WINNING_NUMBERS', 'CLOSED'].includes(drawingState);
  const showTotalWinnings = participationState !== 'VOID' && drawingState !== 'RESULTS_PENDING' && (isCalculating || participationState === 'FINISHED');

  const olderParticipationsInfo = (
    <div
      className="ll-container-inner flex flex-col items-center gap-2 mt-6 text-center"
      data-track-container="Older Transactions"
      data-testid="participation-info-older-participation-help"
    >
      <p>{t('purchase.older_participations_1')}</p>
      <p>{t('purchase.older_participations_2')}</p>
      <Link href={ROUTES.HELP_CENTRE.BASE_URL} target="_blank" rel="noopener" data-testid="help-link" data-track-name="Help">
        {t('purchase.contact_us')}
      </Link>
    </div>
  );

  const onOlderDrawsBtnClick = () => {
    setIsShowingOlderParticipationsInfo(true);
  };

  const handleDateSelect = (date: Date) => {
    setIsShowingOlderParticipationsInfo(false);
    onDateSelect(date);
  };

  const getDrawSelectorVariant = (lotteryName: LotteryName) => {
    const drawFrequency = getLotteryDrawFrequency(lotteryName);
    return drawFrequency === 'NORMAL' ? 'date' : 'highFrequency';
  };

  return (
    <div className={twMerge('ll-grid-container min-w-0 gap-y-6', className)} {...props}>
      <DrawSelector
        variant={getDrawSelectorVariant(lotteryName)}
        items={draws}
        onDateSelect={handleDateSelect}
        olderDraws={hasMoreHistoricalParticipations}
        olderDrawsBtnSelected={isShowingOlderParticipationsInfo}
        onOlderDrawsClick={onOlderDrawsBtnClick}
        className="ll-container-full min-w-0 px-[var(--ll-container-padding)] py-2"
        data-track-container="Dates"
      />
      <DyjDisabledMessage className="ll-container-inner" show={!!(dyjPlayed && dyjCancelled)} data-track-type="DYJ" data-track-message="Refund Sent" />
      {isShowingOlderParticipationsInfo ? (
        olderParticipationsInfo
      ) : (
        <>
          {showTotalWinnings && <TotalWinnings calculating={isCalculating} totalWinnings={totalWinnings} className="ll-container-inner" />}
          <PurchaseResultsSection className="ll-container-inner" drawingId={drawingId} state={participationState} date={new Date(drawingDate)} />
        </>
      )}
    </div>
  );
}

export { ParticipationInfo };
